import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Tooltip from "@mui/material/Tooltip";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import Collapse from "@mui/material/Collapse";
import { zIndexMap } from "../../utils/zIndexMap";
import { PACKAGE_INDICATORS } from "@directv-digital/dds.services.channel-lineup-provider";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import DtvTheme, { colorPalette, MBL_SIZE, TBL_SIZE } from "@directv-digital/dds.ui.dtv-theme";
import { channelColWidthStyle } from "../PackageCmprGrid.styles";

const bps = DtvTheme.breakpoints;

interface ChannelRowProps {
  channel: any;
  chnlPackageData: any[];
  mainColWidthStyle?: { width: string; flexBasis: string };
  packageIndicatorIconMap: any;
  borderColor?: string;
  textColor?: string;
  rsnTierLegalTextObj?: any;
}

const sizing = {
  root: {
    display: "flex",
    width: "100%",
    margin: '0px !important',
    padding: "0px"
  },
  channelColContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: "6px 8px 4px !important",
    textAlign: "center",
    [bps.up(bps.values.sm)]: {
      padding: "8px !important"
    }
  },
  channelIcon: {
    display: "inline-flex",
    verticalAlign: "middle",
    height: "100%", // "58px",
    width: "100%",
    paddingTop: "1.3px" // eyeballed to center svg exactly
  },
  channelIconLocalRsnIndicatorText: {
    display: "block",
    position: "relative",
    maxWidth: "80px",
    maxHeight: "80px",
    borderRadius: "4px",
    m: "-14px auto 7px auto",
    p: "5px"
  },
  channelName: {
    transition: "max-height 300ms ease-in-out, opacity 300ms ease-in-out"
  },
  channelNum: {
    height: '21px'
  },
  indicatorColBorder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: {
      xs: "2px",
      sm: "8px"
    }
  }
};

const MuiTooltipStyleOverrides = {
  position: "relative",
  display: "inline-block",
  maxWidth: "224px",
  width: 'max-content',
  backgroundColor: "#FEFEFE !important",
  color: "#102641 !important",
  fontWeight: "400 !important",
  fontSize: "14px !important",
  boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.15)",
  overflowWrap: "normal"
}

const ChannelRow = ({
  channel,
  chnlPackageData,
  mainColWidthStyle,
  packageIndicatorIconMap,
  borderColor,
  textColor,
  rsnTierLegalTextObj
}: ChannelRowProps) => {
  const {
    name,
    icon
  } = channel;

  const [rsnChannelSeeDetailsClicked, setRsnChannelSeeDetailsClicked] = React.useState(false);

  const [channelTeams, setChannelTeams] = React.useState("");

  React.useEffect(() => {
    if (channel?.category === "RSN") {
      const teams = getRsnTeams(channel);
      setChannelTeams(teams);
    }
  }, [channel]);

  const getRsnTeams = channel => {
    const teams = [];
    if(channel?.nba) {
      const nbaTeams = channel.nba?.split(",") || [];
      nbaTeams.forEach(team => {
        teams.push(team.trim());
      })
    }
    if(channel?.mlb) {
      const mlbTeams = channel.mlb?.split(",") || [];
      mlbTeams.forEach(team => {
        teams.push(team.trim());
      })
    }
    if(channel?.nhl) {
      const nhlTeams = channel.nhl?.split(",") || [];
      nhlTeams.forEach(team => {
        teams.push(team.trim());
      })
    }
    if(channel?.mls) {
      const nflTeams = channel.mls?.split(",") || [];
      nflTeams.forEach(team => {
        teams.push(team.trim());
      })
    }
    if(channel?.wnba) {
      const nflTeams = channel.wnba?.split(",") || [];
      nflTeams.forEach(team => {
        teams.push(team.trim());
      })
    }
    return teams.join(", ");
  }

  const handleRsnChannelSeeDetailsClicked = () => {
    setRsnChannelSeeDetailsClicked(state => !state);
  }

  const ChannelIcon = React.useMemo(() => (
      <DtvBox sx={{
 width: {
 xs: "64px",
sm: "80px"
},
height: {
 xs: "64px",
sm: "80px"
}
}}>
        <img
          alt={name} src={icon}
          style={{
            ...sizing.channelIcon,
            fontSize: "8px",
            fill: "#747474",
            position: "relative",
            zIndex: zIndexMap.ChannelIconImg
          }}
          onError={e => {
            console.error("[PackagesGrid][ChannelRow] untracked icon error", e);
          }}
        />
      </DtvBox>
  ), [icon, name]);

  const DownIcon = () => (
      <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.23092 5.98906C2.23092 5.71233 2.33175 5.43105 2.5378 5.21783C2.94989 4.79592 3.61626 4.79592 4.02396 5.21783L7.77662 9.1103L11.538 5.20875C11.9501 4.7823 12.6121 4.7823 13.0242 5.20875C13.4363 5.6352 13.4363 6.32024 13.0242 6.74669L8.81561 11.111C8.5438 11.3923 8.16678 11.5556 7.77223 11.5556C7.38206 11.5556 7.00065 11.3968 6.72885 11.1155L2.52905 6.7603C2.323 6.54708 2.22217 6.27034 2.22217 5.9936" fill="#1FBCF8"/>
      </svg>
  )

  const UpIcon = () => (
      <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3245 10.011C13.3245 10.2877 13.2193 10.569 13.0176 10.7822C12.6055 11.2041 11.9392 11.2041 11.5315 10.7822L7.7788 6.88971L4.0174 10.7913C3.60531 11.2177 2.94333 11.2177 2.53124 10.7913C2.11915 10.3648 2.11915 9.67979 2.53124 9.25334L6.73981 4.88905C7.01162 4.60777 7.38864 4.44446 7.78319 4.44446C8.17336 4.44446 8.55477 4.60323 8.82657 4.8845L13.0264 9.23972C13.2324 9.45294 13.3333 9.72968 13.3333 10.0064" fill="#1FBCF8"/>
      </svg>
  )

  return (
    <DtvBox>
      <DtvBox id={`ChannelRow-${channel?.uuid}`}
        sx={{
          ...sizing.root,
          minHeight: "128px",
          [bps.down(bps.values.sm)]: {
            minHeight: "108px"
          },
          background: "transparent",
          border: "none",
          borderBottom: `1px solid ${borderColor}`
        }}>
        <DtvBox
          alignItems="center" justifyContent="center"
          sx={{
            ...sizing.channelColContainer,
            border: "none",
            color: "#666666",
            ...channelColWidthStyle
          }}
        >
          <DtvBox sx={{
            zIndex: zIndexMap.channelIcon,
            backgroundColor: "transparent",
            borderRadius: "50%",
            margin: {
              xs: "0 3px 2px 0",
              sm: "0 3px 4px 0",
              md: "0 4px 4px 0"
            }
          }}>
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={2000}
              placement="right-start" title={channel?.name}
              PopperProps={{
 sx: {
                "& .MuiTooltip-tooltip": {
                  ...MuiTooltipStyleOverrides,
                  transform: 'translate(-8px, 50%) !important'
                },
                visibility: {
                  xs: "visible",
                  sm: "hidden"
                }
              }
}}
            >
              {ChannelIcon}
            </Tooltip>
          </DtvBox>
          <>
            {channel?.category === "LOCAL" && (
              <Tooltip
                enterTouchDelay={0}
                leaveTouchDelay={2000}
                placement="right-end" title={"Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them."}
                PopperProps={{
 sx: {
                  "& .MuiTooltip-tooltip": {
                    ...MuiTooltipStyleOverrides,
                    transform: 'translate(-8px, calc(50% + 14px)) !important'
                  }
                }
}}
              >
                <span>
                <DtvText
                  content={channel?.category === "LOCAL" ? "Local ⓘ" : "RSN ⓘ"}
                  variant="smalltext"
                  sx={{
                    ...sizing.channelIconLocalRsnIndicatorText,
                    backgroundColor: "#555555",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "white.main",
                    textAlign: "center",
                    zIndex: zIndexMap.channelIconLocalRsnIndicator // 5,
                  }}
                />
                </span>
              </Tooltip>
            )}
            {channel?.category === "RSN" && (
              <DtvBox sx={{
 display: "flex",
justifyContent: "center",
alignItems: "center",
height: "20px",
minWidth: "20px",
paddingX: "6px",
backgroundColor: "#555555",
borderRadius: "4px"
}}>
                <DtvText content="RSN" variant="label" sx={{
 fontSize: "12px",
lineHeight: "130%",
color: "#FFFFFF"
}} />
              </DtvBox>
            )}
          </>
          {channel?.category === "RSN" ?
            <DtvText content={`${channel?.name}<sup>${channel?.rsntier !== 1 ? channel?.rsntier : ""}</sup>`}
            variant="body2"
            sx={{
              ...sizing.channelName,
              fontSize: "16px",
              fontWeight: 500,
              color: textColor
            }}
          /> :
            <DtvText content={channel?.name}
            variant="body2"
            sx={{
              ...sizing.channelName,
              fontSize: "16px",
              fontWeight: 500,
              color: textColor
            }}
          />
          }

          <DtvText content={`${channel?.chnlNum}`}
            variant="body2"
            sx={{
              ...sizing.channelNum,
              fontSize: "16px",
              fontWeight: 500,
              color: textColor
            }}
          />
          <>
            {channel?.category === "RSN" &&
              <DtvBox sx={{
 display: "flex",
justifyContent: "center",
alignItems: "center",
gap: "2px"
}}>
                {rsnChannelSeeDetailsClicked ? <UpIcon /> : <DownIcon />}
                <DtvButton
                  aria-controls="rsn-details-panel"
                  aria-expanded={rsnChannelSeeDetailsClicked}
                  label="See details"
                  variant="uxlink"
                  sx={{
                    color: "#1FBCF8",
                    textDecoration: "none",
                    fontSize: "12px"
                  }}
                  onClick={handleRsnChannelSeeDetailsClicked}
                />
              </DtvBox>
            }
          </>
        </DtvBox>
        {channel?.indicators?.map(
          (indicator, ix) => {
            const tooltipText = packageIndicatorIconMap?.[indicator]?.tooltipText || "";
            const pkgName = chnlPackageData[ix]?.pkgName || "";

            return (
              <DtvBox
                id={`${channel?.name}-${ix}`}
                key={`${channel?.name}-icon-${ix}`}
                sx={{
                  ...sizing.indicatorColBorder,
                  borderLeft: `1px solid ${borderColor}`,
                  borderBottom: "none",
                  color: "#666666",
                  textAlign: "center",
                  ...mainColWidthStyle
                }}
              >
                {indicator !== null && <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={2000} placement="top"
                  PopperProps={{
 sx: {
                      width: "auto !important",
                      marginBottom: '-10px !important',
                      "& .MuiTooltip-tooltip": {
                        ...MuiTooltipStyleOverrides,
                        transform: 'translate(-10px, 50%)',
                        fontSize: "16px !important"

                      }
                  }
}}
                  title={
                    <DtvBox sx={{ textAlign: "center" }}>
                      <DtvBox sx={{ fontSize: "16px !important" }}>{tooltipText}</DtvBox>
                      <DtvText content={pkgName} sx={{ fontSize: "16px !important" }}/>
                    </DtvBox>
                  }
                >
                  <span>
                  <img
                    alt={packageIndicatorIconMap?.[indicator as PACKAGE_INDICATORS]?.alt(tooltipText, pkgName)}
                    src={packageIndicatorIconMap?.[indicator as PACKAGE_INDICATORS]?.src}
                    style={{
                      width: "30px",
                      verticalAlign: "middle",
                      display: "inline-flex",
                      color: packageIndicatorIconMap?.[indicator as PACKAGE_INDICATORS]?.color
                    }}
                    />
                  </span>
                </Tooltip>}
              </DtvBox>
            );
          }
        )}
      </DtvBox>
      {channel?.category === "RSN" && <Collapse in={rsnChannelSeeDetailsClicked} sx={{ width: "100%" }}>
        {channelTeams && <DtvBox sx={{
 display: "flex",
width: "100%",
backgroundColor: "#555555",
padding: "4px 20px 4px 20px",
gap: "10px",
color: "#FFFFFF"
}}>
          <DtvText content="Teams: " variant="body3medium" />
          <DtvText content={channelTeams} variant="body3" />
        </DtvBox>}
        <DtvBox sx={{
 backgroundColor: "#F4F4F4",
padding: "4px 20px 4px 20px"
}}>
          <DtvText
            content={`<sup>${channel?.rsntier !== 1 ? channel?.rsntier : ""}</sup> ${rsnTierLegalTextObj?.[0]?.stream?.[`tier${channel?.rsntier}`]}`}
            sx={{
 fontSize: "12px !important",
color: "#102641"
}}
          />
        </DtvBox>
      </Collapse>}
    </DtvBox>
  );
};

export const MemoizedChannelRow = React.memo(ChannelRow);
